import { useEffect, useState } from "react";
import { BarChart3 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { money } from "../lib/helpers";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export function ResultStats({ r, id }) {
  const m = (v) => money(v, r.currency);
  return (
    <div className="w-full flex flex-wrap gap-x-5 gap-y-1 text-xs rounded-md bg-primary/5 px-3 py-2" data-testid={`campaign-results-${id}`}>
      <span data-testid={`res-uses-${id}`}><b>{r.uses}</b> code uses</span>
      <span data-testid={`res-bookings-${id}`}><b>{r.bookings}</b> bookings ({r.completed} completed)</span>
      <span data-testid={`res-orders-${id}`}><b>{r.orders}</b> shop orders</span>
      {r.clinic_uses > 0 && <span data-testid={`res-clinic-${id}`}><b>{r.clinic_uses}</b> used in clinic</span>}
      <span data-testid={`res-revenue-${id}`}>Revenue <b>{m(r.revenue)}</b></span>
      <span className="text-muted-foreground" data-testid={`res-discount-${id}`}>Discount given {m(r.discount)}</span>
    </div>
  );
}

function ResultsTable({ rows }) {
  if (!rows.length) return <p className="text-sm text-muted-foreground" data-testid="results-empty">Nobody has used this code yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="results-table">
        <thead><tr className="text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Date</th><th className="pr-3">Who</th><th className="pr-3">What</th><th className="pr-3">Amount</th><th>Discount</th></tr></thead>
        <tbody>{rows.map((r, i) => (
          <tr key={i} className="border-t" data-testid={`results-row-${i}`}>
            <td className="py-2 pr-3 whitespace-nowrap">{r.date}</td><td className="pr-3">{r.name}</td>
            <td className="pr-3">{r.what}{r.status && <span className="ml-1 text-xs text-muted-foreground capitalize">· {r.status.replaceAll("_", " ")}</span>}</td>
            <td className="pr-3 whitespace-nowrap">{r.amount != null ? money(r.amount, r.currency) : "–"}</td>
            <td className="whitespace-nowrap">{r.discount ? money(r.discount, r.currency) : "–"}</td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

export function ResultsButton({ c }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (open) { api.get(`/marketing/campaigns/${c.id}/results`).then((r) => setData(r.data)).catch((e) => setErr(errMsg(e))); }
  }, [open, c.id]);
  return (
    <>
      <Button size="sm" variant="outline" className="rounded-full" data-testid={`results-campaign-${c.id}`} onClick={() => setOpen(true)}><BarChart3 className="h-4 w-4 mr-1" />See results</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="results-dialog">
          <DialogHeader><DialogTitle>{c.name} · code {c.offer_code}</DialogTitle></DialogHeader>
          {err && <p className="text-sm text-destructive">{err}</p>}
          {data && <div className="space-y-4"><ResultStats r={data.summary} id="dialog" /><ResultsTable rows={data.rows} /></div>}
        </DialogContent>
      </Dialog>
    </>
  );
}
